import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import classnames from 'classnames';

import { KeyMetrics } from '../components/key-metrics';
import { useModal } from '../../../hooks/use-modal';
import NullStateOverlay from '../components/null-state-overlay';
import InsufficientDataBannerMessage from '../components/insufficient-data-banner-message';
import NewOrdersGraph from '../components/graphs/new-orders-graph';
import OpenOrderAgingGraph from '../components/graphs/open-order-aging-graph';
import ChangeSlaModal from '../components/graphs/open-order-aging-graph/change-sla-modal';
import { ShipmentsByCarrierTable } from '../components/shipments-by-carrier-table';
import { SalesByStoreTable } from '../components/sales-by-store-table';
import { doubleDash } from '../constants';

import messages from './messages';
import style from './style.module.less';
import analyticsStyle from '../analytics-style.module.less';

export const AnalyticsOrdersOverviewReport = ({ chartData }) => {
  const { open, close, isOpen } = useModal();
  const { orderByAge, orderByInterval, orderByStore, orderSummary, shipmentByCarrier } = chartData || {};
  const shouldShowNullOverlay = !orderSummary?.totalOrderCount;
  const { totalOrderCount = 0, awaitingShipmentCount = 0, shippedCount = 0 } = orderSummary || {};
  const dataIsUndefined = totalOrderCount === undefined;

  const keyMetricsData = useMemo(
    () => [
      {
        id: 1,
        title: <FormattedMessage {...messages.newOrdersLabel} />,
        value: dataIsUndefined ? doubleDash : <FormattedNumber value={totalOrderCount} />,
      },
      {
        id: 2,
        title: <FormattedMessage {...messages.ordersShippedLabel} />,
        value: dataIsUndefined ? doubleDash : <FormattedNumber value={shippedCount} />,
      },
      {
        id: 3,
        title: <FormattedMessage {...messages.ordersUnshippedLabel} />,
        value: dataIsUndefined ? doubleDash : <FormattedNumber value={awaitingShipmentCount} />,
      },
    ],
    [dataIsUndefined, totalOrderCount, shippedCount, awaitingShipmentCount],
  );

  return (
    <>
      <KeyMetrics data={keyMetricsData} />
      {shouldShowNullOverlay && <InsufficientDataBannerMessage />}
      <div className={classnames(style['new-orders-graph'], analyticsStyle['analytics-section-wrapper'])}>
        {shouldShowNullOverlay && <NullStateOverlay />}
        <NewOrdersGraph data={orderByInterval} />
      </div>
      <div className={classnames(style.container, analyticsStyle['analytics-container'])}>
        <div className={analyticsStyle['analytics-section-wrapper']}>
          <SalesByStoreTable data={orderByStore} />
        </div>
        <div className={analyticsStyle['analytics-section-wrapper']}>
          <OpenOrderAgingGraph data={orderByAge} onChangeSla={open} width='100%' />
          {isOpen && <ChangeSlaModal onClose={close} />}
        </div>
      </div>
      <div className={classnames(analyticsStyle['analytics-section-wrapper'], analyticsStyle['analytics-container'])}>
        <ShipmentsByCarrierTable data={shipmentByCarrier} />
        <p>
          <Link to='/dashboard/reports/ShippingExtract'>
            <FormattedMessage {...messages.allShippedDataLink} />
          </Link>
        </p>
      </div>
    </>
  );
};
